import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { tokenStore } from '../lib/tokenStore';
import Unterschrift from '@/components/Unterschrift';
import SendenDialog from '@/components/unterschrift/SendenDialog';
import MitgliedSuche from '@/components/MitgliedSuche';
import UpsellVerkauf from '@/components/UpsellVerkauf';
import api, { unterschriftAPI } from '../lib/api';
import {
  Check, ChevronRight, ChevronDown, Loader2, CheckCircle2, Star, Gift, Sparkles,
  User, Baby, Users, FileSignature, ArrowLeft,
  Target, Dumbbell, Heart, Shield, TrendingUp, Flame, Calendar,
  Clock, MessageSquare, AlertCircle, Save, ClipboardList, Download
} from 'lucide-react';
import { useBankAusIban, useOrtAusPlz, OrtVorschlaege, BankHinweis } from '@/lib/stammdaten';
import {
  TARIFE, TARIF_BY_ID, LAUFZEIT_LABEL, LAUFZEITEN, EMPFOHLENE_LAUFZEIT, LINIE_LABEL, laufzeitenFuer, istOhneBausteine,
  BETREUTER_EINSTIEG, CHECKUP, CHECKUP_INKLUSIVE, FERIEN_EVENT_PAUSCHALE, GESCHWISTER, laufzeitMonate, GYM_SAUNA, ZWEITE_EINHEIT, wochenToVierWochen, planId, empfehleTarif, fmtEuro, vorauszahlerInfo,
} from '../lib/tarife';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ ...tokenStore.getAuthHeaders(), 'Content-Type': 'application/json' });
function fmt(v) { return (v ?? 0).toFixed(2).replace('.', ','); }

const GOALS = [
  { id: 'muskelaufbau', label: 'Muskelaufbau', icon: Dumbbell },
  { id: 'abnehmen', label: 'Abnehmen', icon: Flame },
  { id: 'gesundheit', label: 'Gesundheit', icon: Heart },
  { id: 'selbstverteidigung', label: 'Selbstverteidigung', icon: Shield },
  { id: 'leistung', label: 'Leistung / Wettkampf', icon: TrendingUp },
  { id: 'stressabbau', label: 'Stressabbau', icon: Target },
];
const ZIELGRUPPEN = [
  { id: 'erwachsener', label: 'Erwachsener', icon: User },
  { id: 'kind', label: 'Kind / Jugend', icon: Baby },
  { id: 'familie', label: 'Familie', icon: Users },
];
const FREQUENZEN = [
  { id: '1x', label: '1× / Woche' },
  { id: '2x', label: '2× / Woche' },
  { id: 'unbegrenzt', label: 'Unbegrenzt' },
];
const PRIORITIES = [
  { id: 'hoch', label: 'Hoch' }, { id: 'mittel', label: 'Mittel' }, { id: 'niedrig', label: 'Niedrig' },
];
const CHANNELS = [
  { id: 'telefon', label: 'Telefon' }, { id: 'email', label: 'E-Mail' },
  { id: 'persoenlich', label: 'Persönlich' }, { id: 'whatsapp', label: 'WhatsApp' },
];

const STEPS = [
  { n: 1, label: 'Bedarf' },
  { n: 2, label: 'Empfehlung' },
  { n: 3, label: 'Tag 1 Angebot' },
  { n: 4, label: 'Vertrag' },
  { n: 5, label: 'Follow-up' },
];

export default function VerkaufPage({ vorbelegung }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  // Upsell ohne neuen Vertrag (Betreiber 22.10.2026): Baustein zur laufenden Mitgliedschaft, Abschluss per QR-Code
  const [modus, setModus] = useState(vorbelegung?.modus === 'upsell' || new URLSearchParams(window.location.search).get('modus') === 'upsell' ? 'upsell' : 'vertrag');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Vorbefuellung aus "Abschluss heute": der Closer springt mit Name,
  // Kontakt und Zielgruppe des Neukunden direkt ins Gespraech — am
  // Empfangs-Tablet tippt niemand Daten ab, die das CRM schon kennt.
  const [urlParams] = useSearchParams();
  // Eingebettet im Empfang kommt die Vorbelegung als Prop statt aus der URL
  const vb = (feld) => (vorbelegung && vorbelegung[feld]) || urlParams.get(feld);

  // Step 1: Bedarf
  const [zielgruppe, setZielgruppe] = useState(
    ['erwachsener', 'kind', 'familie'].includes(vb('zielgruppe'))
      ? vb('zielgruppe') : 'erwachsener');
  // Abschluss-Check-up (Coach-Cockpit W1.6b): `?frequenz=` aus dem Weg ab jetzt
  const [frequenz, setFrequenz] = useState(['1x', '2x', 'unbegrenzt'].includes(vb('frequenz')) ? vb('frequenz') : '2x');
  const [gymSauna, setGymSauna] = useState(false);
  const [vorerfahrung, setVorerfahrung] = useState('nein');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [leadName, setLeadName] = useState(vb('name') || '');
  const [leadPhone, setLeadPhone] = useState(vb('telefon') || '');
  const [leadEmail, setLeadEmail] = useState(vb('email') || '');
  // Abschluss-Check-up (W1.6b): Vorbelegung über `?lead=<id>` — Name und Kontakt kommen vom Server, nicht aus der Adresse
  const vorLead = vb('lead');
  useEffect(() => {
    if (!vorLead) return;
    api.get(`/leads/${vorLead}`).then((r) => {
      const l = r.data || {};
      setLeadName((x) => x || l.name || ''); setLeadPhone((x) => x || l.phone || ''); setLeadEmail((x) => x || l.email || '');
    }).catch(() => {});
  }, [vorLead]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [leadPriority, setLeadPriority] = useState('mittel');
  const [leadNotes, setLeadNotes] = useState('');

  // Step 2: Empfehlung
  const [selectedTarifId, setSelectedTarifId] = useState(null);
  const [laufzeit, setLaufzeit] = useState(EMPFOHLENE_LAUFZEIT);
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Step 3: Tag 1
  const [checkup, setCheckup] = useState('none'); // none | standard | intensiv
  const [famKind2, setFamKind2] = useState(false);  // Family: Kind auf 2×/Woche
  const [famErw2, setFamErw2] = useState(false);    // Family: Erwachsener auf 2×/Woche
  const [geschwister, setGeschwister] = useState(false); // -25 %, Kindertarif ab 12 Monaten
  // Individuelle Vereinbarung (26.10.2026, Kinder und Erwachsene): freier Monatsbeitrag mit Grund statt Listenpreis
  const [indiv, setIndiv] = useState(false);
  const [indivBeitrag, setIndivBeitrag] = useState('');
  const [indivGrund, setIndivGrund] = useState('');
  const [tag1Abschluss, setTag1Abschluss] = useState(true);
  const [vorauszahler, setVorauszahler] = useState(false);

  // Step 4: Vertrag
  const [contractType, setContractType] = useState('erwachsene'); // erwachsene | kinder | family
  const [form, setForm] = useState({
    vorname: '', nachname: '', geburtsdatum: '', strasse: '',
    plz: '', ort: '', telefon: '', email: '',
    kontoinhaber: '', iban: '', bic: '', kreditinstitut: '',
    vertragsbeginn: new Date().toISOString().split('T')[0],
    notizen: '',
    erziehungsberechtigter_name: '', erziehungsberechtigter_telefon: '', erziehungsberechtigter_email: '',
    eigenes_konto: false,   // 02.11.2026: Jugendliche ab 16 buchen selbst (eigener App-Login, kein Elternkonto)
    agb_akzeptiert: false, sepa_akzeptiert: false,
    ziel_satz: '',   // v6 (M7): Pflicht — startet die Onboarding-Kette mit Sprechtexten
    eltern_zustimmung: false,   // Kinder/Family: Zustimmung der/des Erziehungsberechtigten (Pflicht)
    unterschrift_mitglied: null, unterschrift_eltern: null, abschluss_ort: 'studio', widerruf_akzeptiert: false, einwilligungen_client: {},
  });

  // Step 5: Follow-up
  const [followUp, setFollowUp] = useState({ date: '', time: '10:00', channel: 'telefon', subject: '', notes: '', priority: 'mittel' });
  const [isFollowUpOnly, setIsFollowUpOnly] = useState(false);
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  // Verkaufsrechner mit dem Lead (30.10.2026): /verkauf?lead=<id> lädt, was das CRM schon weiß — nichts wird doppelt getippt
  const [vorb, setVorb] = useState(null);
  const leadParam = vb('lead');
  useEffect(() => {
    if (!leadParam) return undefined;
    let aktiv = true;
    api.get(`/vertraege/vorbelegung/${encodeURIComponent(leadParam)}`).then((r) => {
      if (!aktiv) return;
      const v = r.data;
      setVorb(v);
      const name = `${v.vorname || ''} ${v.nachname || ''}`.trim();
      if (name) setLeadName(name);
      if (v.telefon) setLeadPhone(v.telefon);
      if (v.email) setLeadEmail(v.email);
      if (v.kind) setZielgruppe('kind');
      setTag1Abschluss(!!v.tag1_gilt);              // ein laufendes Tag-1-Angebot ist vorausgewählt, sonst nicht
      setForm((f) => ({ ...f,
        vorname: v.vorname || f.vorname, nachname: v.nachname || f.nachname, geburtsdatum: v.geburtsdatum || f.geburtsdatum,
        strasse: v.strasse || f.strasse, plz: v.plz || f.plz, ort: v.ort || f.ort, telefon: v.telefon || f.telefon, email: v.email || f.email,
        erziehungsberechtigter_name: v.erziehungsberechtigter_name || f.erziehungsberechtigter_name,
        erziehungsberechtigter_telefon: v.erziehungsberechtigter_telefon || f.erziehungsberechtigter_telefon,
        erziehungsberechtigter_email: v.erziehungsberechtigter_email || f.erziehungsberechtigter_email,
        kontoinhaber: v.kontoinhaber || f.kontoinhaber, iban: v.iban || f.iban, ziel_satz: v.ziel_satz || f.ziel_satz }));
    }).catch(() => { if (aktiv) toast.info('Der Lead konnte nicht geladen werden — bitte die Daten von Hand eintragen'); });
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadParam]);

  const topRef = useRef(null);
  const isKidsLike = contractType !== 'erwachsene';
  // Alter am heutigen Tag aus dem Geburtsdatum (JJJJ-MM-TT) — nur für das Angebot „eigenes App-Konto“ ab 16; der Server prüft selbst
  const alterHeute = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(form.geburtsdatum || '');
    if (!m) return null;
    const h = new Date();
    return h.getFullYear() - +m[1] - ((h.getMonth() + 1 < +m[2] || (h.getMonth() + 1 === +m[2] && h.getDate() < +m[3])) ? 1 : 0);
  })();
  const eigenesKontoMoeglich = contractType === 'kinder' && alterHeute !== null && alterHeute >= 16 && alterHeute < 18;
  // Erklaerungen aus dem Einwilligungsmodul (Haftung, Hausordnung, Datenverarbeitung, Foto/Video)
  const [erklaerungen, setErklaerungen] = useState([]);
  const [erklaerungHaken, setErklaerungHaken] = useState({});
  const [erklaerungOffen, setErklaerungOffen] = useState(null);
  /* 01.10.2026: Laedt die Liste nicht (Server gerade im Neustart, Netz weg), fehlten die Haken
     stillschweigend — der Server lehnte den Abschluss dann mit „Erklärung fehlt“ ab. Jetzt
     sichtbar mit „Erneut laden“, und ohne geladene Erklärungen wird nicht abgeschickt. */
  const [erklaerungenFehler, setErklaerungenFehler] = useState(false);
  const ladeErklaerungen = useCallback(() => {
    api.get('/vertraege/erklaerungen')
      .then(r => { const l = r.data.erklaerungen || []; setErklaerungen(l); setErklaerungenFehler(!l.length); })
      .catch(() => { setErklaerungen([]); setErklaerungenFehler(true); });
  }, []);
  useEffect(() => {
    if (step !== 4 || erklaerungen.length) return;
    ladeErklaerungen();
  }, [step, erklaerungen.length, ladeErklaerungen]);
  const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const bankErkannt = useBankAusIban(form.iban, b => setForm(f => ({ ...f, ...b })));
  const ortListe = useOrtAusPlz(form.plz, form.ort, v => updateForm('ort', v));
  // Probetraining vorher? (29.09.2026) — sonst zaehlt der Abschluss nicht in die Quote
  const [pt, setPt] = useState({ wahl: '', am: '' });   // wahl: 'b:<id>' | 'l:<id>' | 'datum' | 'ohne'
  const [ptKandidaten, setPtKandidaten] = useState([]);
  useEffect(() => {
    if (step !== 4) return;
    const h = setTimeout(async () => {
      try {
        const r = await api.get('/probetrainings/kandidaten', { params: {
          telefon: form.telefon, email: form.email, nachname: form.nachname,
          eltern_telefon: form.erziehungsberechtigter_telefon, eltern_email: form.erziehungsberechtigter_email } });
        setPtKandidaten(r.data.kandidaten || []);
      } catch { setPtKandidaten([]); }
    }, 400);
    return () => clearTimeout(h);
  }, [step, form.telefon, form.email, form.nachname, form.erziehungsberechtigter_telefon, form.erziehungsberechtigter_email]);
  const [werber, setWerber] = useState(null);   // Empfehlungsaktion 30.09.2026: wer hat geworben?
  const ptFelder = () => {
    if (pt.wahl.startsWith('b:')) return { probetraining_art: 'buchung', probetraining_buchung_id: pt.wahl.slice(2) };
    if (pt.wahl.startsWith('l:')) return { probetraining_art: 'lead', probetraining_lead_id: pt.wahl.slice(2) };
    if (pt.wahl === 'datum') return { probetraining_art: 'datum', probetraining_am: pt.am };
    return { probetraining_art: 'ohne' };
  };
  // Beweisbarer Abschluss: Zeitpunkt je Haken (Client), Server setzt seine eigene Zeit dazu
  const haken = (k, schluessel) => (v) => setForm(f => ({ ...f, [k]: v,
    einwilligungen_client: { ...(f.einwilligungen_client || {}), [schluessel]: v ? new Date().toISOString() : null } }));
  const updateFU = (k, v) => setFollowUp(f => ({ ...f, [k]: v }));
  const toggleGoal = (id) => setSelectedGoals(p => p.includes(id) ? p.filter(g => g !== id) : [...p, id]);

  // Empfehlung ableiten — Standard immer 2× (Performance/Performance Kids/Family)
  const empfohlenerTarifId = useMemo(
    () => empfehleTarif({ zielgruppe, frequenz, gymSauna }),
    [zielgruppe, frequenz, gymSauna]
  );

  // Aktueller Tarif + Preise
  const tarif = selectedTarifId ? TARIF_BY_ID[selectedTarifId] : null;
  // Kinder/Family haben kein Flex — faellt die gewaehlte Laufzeit weg, greift 12 Monate
  const basisWpreis = tarif ? (tarif.wochenpreis[laufzeit] ?? tarif.wochenpreis.m12 ?? laufzeitenFuer(tarif).map(lz => tarif.wochenpreis[lz])[0] ?? 0) : 0;
  // Sondertarif (P1 Minis): keine Bausteine — Einstieg, Check-Ups, Pauschale, Rabatte entfallen
  const ohneBausteine = istOhneBausteine(tarif);
  // Family: zweite Einheit je Person als Add-on (Family Plus ist gestrichen)
  const istFamily = tarif?.linie === 'family';
  const famZuschlag = istFamily ? (famKind2 ? ZWEITE_EINHEIT.kinder : 0) + (famErw2 ? ZWEITE_EINHEIT.erwachsene : 0) : 0;
  // Geschwisterrabatt: nur Kinderlinie, Laufzeit ab 12 Monaten. Er gehoert auf den
  // Vertrag des GUENSTIGEREN Kindes — das teurere zahlt voll.
  const geschwisterMoeglich = tarif?.linie === 'kinder' && laufzeitMonate(laufzeit) >= GESCHWISTER.abMonaten && !ohneBausteine;
  const indivMoeglich = !!tarif;            // seit 26.10.2026 alle Tarife (vorher nur Kinder)
  const indivWert = Number(String(indivBeitrag).replace(',', '.'));
  const indivAktiv = indiv && indivMoeglich && indivWert > 0;
  const geschwisterAktiv = geschwister && geschwisterMoeglich && !indivAktiv;
  const geschwisterAbzug = geschwisterAktiv ? Math.round(basisWpreis * GESCHWISTER.rabatt * 100) / 100 : 0;
  const wpreis = indivAktiv ? Math.round((indivWert / 4) * 100) / 100   // Monatsbeitrag = 4 Wochen (wie der Server)
    : Math.round((basisWpreis + famZuschlag - geschwisterAbzug) * 100) / 100;
  const vierWochen = wochenToVierWochen(wpreis);
  const pid = tarif ? planId(tarif.id, laufzeit) : null;
  const durationLabel = LAUFZEIT_LABEL[laufzeit];
  const bindungMonate = laufzeit === 'm24' ? 24 : laufzeit === 'm12' ? 12 : laufzeit === 'm6' ? 6 : 0;
  // Check-Up-Kontingent: 12 Monate -> 4, 24 Monate -> 8 Standard-Termine je Vereinbarung
  const checkupKontingent = ohneBausteine ? 0 : (CHECKUP_INKLUSIVE[laufzeit] ?? 0);
  const checkupInklusive = checkupKontingent > 0;
  const gymSaunaInklusive = tarif?.id === 'performance_plus';
  // Ferien- & Event-Pauschale: 1 Monatsbeitrag jaehrlich zum 1. August, nur mit Bindung
  const pauschaleGilt = FERIEN_EVENT_PAUSCHALE.nurLaufzeiten.includes(laufzeit) && !ohneBausteine;
  const pauschaleText = tarif?.linie === 'family'
    ? 'Open Mat in den Ferien & alle Seminare inklusive · Kindercamps kostenfrei'
    : tarif?.linie === 'kinder'
      ? 'Kindercamps kostenfrei, mehrfach im Jahr'
      : 'Open Mat in den Ferien & alle Seminare inklusive';
  const gymSaunaAktiv = gymSauna && !gymSaunaInklusive;

  // Einstiegssatz nach Linie: Kinder eigener Satz, Family = Erwachsener + Kind
  const einstiegSatz = ohneBausteine ? { regulaer: 0, tag1: 0 }
    : tarif?.linie === 'kinder' ? BETREUTER_EINSTIEG.kinder
    : tarif?.linie === 'family'
      ? { regulaer: BETREUTER_EINSTIEG.regulaer + BETREUTER_EINSTIEG.kinder.regulaer,
          tag1: BETREUTER_EINSTIEG.tag1 + BETREUTER_EINSTIEG.kinder.tag1 }
      : BETREUTER_EINSTIEG;
  const einstiegPreis = tag1Abschluss ? einstiegSatz.tag1 : einstiegSatz.regulaer;
  const checkupPreis = checkup === 'none' ? 0
    : (checkupInklusive && checkup === 'standard') ? 0
    : CHECKUP[checkup].preis;
  const vzInfo = tarif && laufzeit !== 'flex' && !ohneBausteine && !indivAktiv ? vorauszahlerInfo(wpreis, laufzeit) : null;
  // Tarif ohne die gewaehlte Laufzeit (z. B. Minis nur 6 Monate): auf die erste verfuegbare springen
  useEffect(() => {
    if (tarif && tarif.wochenpreis[laufzeit] == null) { const erste = laufzeitenFuer(tarif)[0]; if (erste) setLaufzeit(erste); }
  }, [tarif, laufzeit]);

  // Empfehlung zum Mitgeben — ersetzt die gedruckten Preisseiten der Beratungsmappe.
  // Der Rechner kennt alles schon, das PDF wird nur gezeichnet (Backend: routes/empfehlung.py).
  const [pdfLaeuft, setPdfLaeuft] = useState(false);
  const empfehlungPdf = async () => {
    if (!tarif) { toast.error('Bitte zuerst einen Tarif wählen'); return; }
    setPdfLaeuft(true);
    try {
      const res = await fetch(`${API}/api/empfehlung/pdf`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({
          name: leadName,
          ziele: selectedGoals.map(id => GOALS.find(g => g.id === id)?.label).filter(Boolean),
          zielgruppe: ZIELGRUPPEN.find(z => z.id === zielgruppe)?.label || '',
          frequenz: FREQUENZEN.find(f => f.id === frequenz)?.label || '',
          tarif_name: tarif.name,
          laufzeit_label: durationLabel,
          wochenpreis: wpreis,
          vier_wochen: vierWochen,
          leistungen: [tarif.einheiten, ...(tarif.leistungen || [])].filter(Boolean),
          einstieg_betrag: einstiegPreis,
          einstieg_tag1: tag1Abschluss,
          checkup_text: checkup === 'none' ? ''
            : `${checkup === 'standard' ? 'Standard' : 'Intensiv'}, ${CHECKUP[checkup].minuten} Min · ${checkupPreis === 0 ? 'inklusive' : fmt(checkupPreis) + ' €'}`,
          vorauszahler_text: (vorauszahler && vzInfo)
            ? `${fmt(vzInfo.betrag)} € im Voraus · ${fmt(vzInfo.ersparnis)} € gespart` : '',
          startdatum: startDate ? new Date(startDate).toLocaleDateString('de-DE') : '',
          berater: user?.name || '',
          notiz: leadNotes || '',
        }),
      });
      if (!res.ok) throw new Error('Serverfehler');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Empfehlung konnte nicht erstellt werden');
    } finally {
      setPdfLaeuft(false);
    }
  };

  const goStep = (n) => { setStep(n); topRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  const linieToContractType = (linie) => (linie === 'erwachsene' ? 'erwachsene' : linie);

  const goToEmpfehlung = () => {
    if (!leadName || !leadPhone) { toast.error('Bitte Name und Telefon angeben'); return; }
    const tid = empfohlenerTarifId;
    setSelectedTarifId(tid);
    setLaufzeit(EMPFOHLENE_LAUFZEIT);
    setContractType(linieToContractType(TARIF_BY_ID[tid].linie));
    goStep(2);
  };

  const prefillContract = () => {
    const [vn, ...nn] = leadName.trim().split(' ');
    // Namen aus den Feldern des Leads, wo vorhanden — nicht am Leerzeichen geteilt
    setForm(f => ({ ...f, vorname: vorb?.vorname || vn || '', nachname: vorb?.nachname || nn.join(' ') || '', telefon: leadPhone, email: leadEmail, vertragsbeginn: startDate }));
  };

  // Der Vertragsentwurf, wie ihn der Server braucht — EINE Quelle fuer den Abschluss am Geraet
  // und fuer „Zur Unterschrift senden" (Unterschrift-Station, 03.10.2026)
  const entwurfBasis = () => ({
    ...form, plan_id: pid, category: contractType, eigenes_konto: eigenesKontoMoeglich && !!form.eigenes_konto,
    betreuter_einstieg: true, tag1_angebot: tag1Abschluss, checkup, vorauszahler,
    // Bausteine gehoeren an den Vertrag, nicht nur in die Notizen —
    // sonst rechnet das Backend einen anderen Beitrag als der Rechner.
    gym_sauna: gymSaunaAktiv,
    zweite_einheit_kind: istFamily && famKind2,
    zweite_einheit_erwachsener: istFamily && famErw2,
    geschwister_rabatt: geschwisterAktiv,
    individueller_beitrag: indivAktiv ? indivWert : null,
    individuell_grund: indivAktiv ? indivGrund.trim() : null,
    ...ptFelder(),
    geworben_von: werber?.id || null,
    ...(vorb?.lead_id ? { lead_id: vorb.lead_id } : {}),     // der Vertrag hängt an DIESEM Lead, nicht an einer E-Mail-Suche
  });
  // Fuer die Station: ohne Haken und Unterschriften — die setzt der Kunde am iPad bzw. Handy
  const entwurfFuerStation = () => {
    const { unterschrift_mitglied, unterschrift_eltern, agb_akzeptiert, sepa_akzeptiert, eltern_zustimmung,
      einwilligungen_client, widerruf_akzeptiert, abschluss_ort, ...rest } = entwurfBasis();
    return rest;
  };
  const [uvVorgang, setUvVorgang] = useState(null);
  const [uvSendet, setUvSendet] = useState(false);
  const uvStand = useRef(null);       // Entwurf zum Zeitpunkt des Sendens (eingefroren)

  const sendeZurUnterschrift = async () => {
    if (!pid || submitting || uvSendet) return;      // nie parallel zum Abschluss am Geraet (waere eine Umvertragung)
    if (indiv && indivMoeglich && (!indivAktiv || indivGrund.trim().length < 3)) { toast.error('Individuelle Vereinbarung: Monatsbeitrag und Grund eintragen'); return; }
    if (!form.vorname || !form.nachname || !form.email || !form.telefon) { toast.error('Bitte alle Pflichtfelder ausfüllen'); return; }
    if (!form.kontoinhaber || !form.iban) { toast.error('SEPA-Daten erforderlich'); return; }
    if (isKidsLike && !(form.erziehungsberechtigter_name || '').trim()) { toast.error('Name der/des Erziehungsberechtigten fehlt'); return; }
    if (isKidsLike && !(form.erziehungsberechtigter_telefon || form.erziehungsberechtigter_email)) { toast.error('Telefon oder E-Mail der/des Erziehungsberechtigten fehlt'); return; }
    if ((form.ziel_satz || '').trim().length < 5) { toast.error('Ziel des Mitglieds in einem Satz eintragen (Pflicht fürs Onboarding)'); return; }
    if (!pt.wahl) { toast.error('Bitte angeben, ob es vorher ein Probetraining gab'); return; }
    if (pt.wahl === 'datum' && !pt.am) { toast.error('Datum des Probetrainings eintragen'); return; }
    setUvSendet(true);
    try {
      const entwurf = entwurfFuerStation();
      const r = await unterschriftAPI.senden(entwurf);
      uvStand.current = JSON.stringify(entwurf);
      setUvVorgang(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Der Vertrag konnte nicht zur Unterschrift gesendet werden');
    }
    setUvSendet(false);
  };
  const uvFertig = (ergebnis) => {
    toast.success(ergebnis.message || 'Vertrag unterschrieben');
    uvStand.current = null;
    setUvVorgang(null);
    setResult(ergebnis);
    goStep(5);
  };
  // Eingefroren heisst eingefroren: aendert sich der Entwurf, waehrend ein Vorgang offen ist,
  // wird der Vorgang abgebrochen und muss neu gesendet werden.
  const entwurfJetzt = uvVorgang ? JSON.stringify(entwurfFuerStation()) : null;
  useEffect(() => {
    if (!uvVorgang || !uvStand.current || entwurfJetzt === uvStand.current) return;
    const id = uvVorgang.id;
    uvStand.current = entwurfJetzt;       // nur einmal je Aenderung
    unterschriftAPI.abbrechen(id, 'Entwurf im Verkaufsrechner geändert')
      .then((r) => r.data?.status)
      .catch(() => 'abgebrochen')
      .then((status) => {
        // Der Kunde war schneller: Vertrag entsteht oder steht — Dialog offen lassen, er meldet das Ergebnis
        if (status === 'wird_abgeschlossen' || status === 'unterschrieben') return;
        uvStand.current = null;
        setUvVorgang((v) => (v && v.id === id ? null : v));
        toast.info('Der Entwurf wurde geändert — der Vorgang ist abgebrochen. Bitte neu zur Unterschrift senden.');
      });
  }, [entwurfJetzt, uvVorgang]);

  const handleSubmit = async () => {
    if (indiv && indivMoeglich && (!indivAktiv || indivGrund.trim().length < 3)) { toast.error('Individuelle Vereinbarung: Monatsbeitrag und Grund eintragen'); return; }
    if (!pid) return;
    if (!form.vorname || !form.nachname || !form.email || !form.telefon) { toast.error('Bitte alle Pflichtfelder ausfüllen'); return; }
    if (!form.kontoinhaber || !form.iban) { toast.error('SEPA-Daten erforderlich'); return; }
    if (!form.agb_akzeptiert || !form.sepa_akzeptiert) { toast.error('AGB und SEPA-Mandat erforderlich'); return; }
    if (isKidsLike && !(form.erziehungsberechtigter_name || '').trim()) { toast.error('Name der/des Erziehungsberechtigten fehlt'); return; }
    if (isKidsLike && !form.eltern_zustimmung) { toast.error('Zustimmung der/des Erziehungsberechtigten fehlt'); return; }
    if ((form.ziel_satz || '').trim().length < 5) { toast.error('Ziel des Mitglieds in einem Satz eintragen (Pflicht fürs Onboarding)'); return; }
    if (!erklaerungen.length) { toast.error('Die Erklärungen (Haftung, Hausordnung, Datenschutz) wurden nicht geladen — bitte „Erklärungen erneut laden“ drücken'); ladeErklaerungen(); return; }
    const pflichtFehlt = erklaerungen.filter(e => e.pflicht && !erklaerungHaken[e.type]).map(e => e.title);
    if (pflichtFehlt.length) { toast.error('Erklärung fehlt: ' + pflichtFehlt.join(', ')); return; }
    if (!pt.wahl) { toast.error('Bitte angeben, ob es vorher ein Probetraining gab'); return; }
    if (pt.wahl === 'datum' && !pt.am) { toast.error('Datum des Probetrainings eintragen'); return; }
    if (isKidsLike && !(form.erziehungsberechtigter_telefon || form.erziehungsberechtigter_email)) { toast.error('Telefon oder E-Mail der/des Erziehungsberechtigten fehlt'); return; }
    if (!form.unterschrift_mitglied) { toast.error('Unterschrift des Mitglieds fehlt'); return; }
    if (isKidsLike && !form.unterschrift_eltern) { toast.error('Unterschrift der/des Erziehungsberechtigten fehlt'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/vertraege/create`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ ...entwurfBasis(), geraet: navigator.userAgent, erklaerungen: erklaerungHaken }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Vertrag erstellt!'); setResult(data); goStep(5); }
      else toast.error(data.detail || 'Fehler');
    } catch { toast.error('Netzwerkfehler'); }
    setSubmitting(false);
  };

  // Modus B (30.10.2026): „Nächsten Schritt festlegen“ nach dem Vertrag speichert wirklich — ein Follow-up am Lead des Vertrags
  const [schrittGespeichert, setSchrittGespeichert] = useState(false);
  const speichereNaechstenSchritt = async () => {
    if (!followUp.date) { toast.error('Bitte ein Datum für den nächsten Schritt angeben'); return; }
    const leadId = result?.lead_id || result?.vertrag?.lead_id;
    if (!leadId) { toast.error('Der Lead zum Vertrag wurde nicht gefunden'); return; }
    setSavingFollowUp(true);
    try {
      const res = await fetch(`${API}/api/follow-ups`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ lead_id: leadId, due_date: `${followUp.date}T${followUp.time || '10:00'}:00`, channel: followUp.channel,
          subject: followUp.subject || 'Nächster Schritt nach Vertragsabschluss', notes: followUp.notes, priority: followUp.priority, assigned_to: user?.name || '' }),
      });
      if (!res.ok) throw new Error('Serverfehler');
      setSchrittGespeichert(true);
      toast.success('Nächster Schritt gespeichert');
    } catch { toast.error('Der nächste Schritt konnte nicht gespeichert werden'); }
    setSavingFollowUp(false);
  };

  const handleSaveFollowUp = async () => {
    if (!followUp.date) { toast.error('Bitte Follow-up Datum angeben'); return; }
    if (!leadName || !leadPhone) { toast.error('Kontaktdaten fehlen'); return; }
    setSavingFollowUp(true);
    try {
      // Erst suchen, dann anlegen (30.10.2026): normalisierte Nummer und E-Mail, wie Schnellerfassung und Funnel — der
      // vorhandene Lead wird ergänzt, es entsteht keine Dublette. Kommt der Lead schon aus dem Rechner-Aufruf, wird er genommen.
      const leadRes = vorb?.lead_id ? { ok: true, json: async () => ({ id: vorb.lead_id, vorhanden: true }) } : await fetch(`${API}/api/leads/finden-oder-anlegen`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({
          name: leadName, phone: leadPhone, email: leadEmail,
          status: 'probetraining', priority: leadPriority,
          notes: `Tarif-Interesse: ${tarif?.name || '?'} (${durationLabel})${famZuschlag > 0 ? ` + zweite Einheit ${[famKind2 && 'Kind', famErw2 && 'Erwachsener'].filter(Boolean).join(' + ')} (+${famZuschlag} €/Wo)` : ''}\nZiele: ${selectedGoals.join(', ') || 'k.A.'}\nCheck-Up: ${checkup}\nGym & Sauna Add-on: ${gymSaunaAktiv ? 'ja (+5 €/Woche)' : gymSaunaInklusive ? 'inklusive (Plus)' : 'nein'}\nBetreuter Einstieg: regulär ${einstiegSatz.regulaer} € (kein Tag-1-Abschluss)\n${leadNotes}`,
          source: 'verkaufsrechner',
          // Baustein 1: wer hier sitzt, steht im Studio — Laufkundschaft
          herkunft: { kanal: 'laufkundschaft' },
          assigned_to: user?.name || '',
        }),
      });
      const leadData = await leadRes.json();
      const leadId = leadData?.id || leadData?.lead_id;
      if (!leadRes.ok || !leadId) { toast.error(leadData?.detail || 'Der Lead konnte nicht gespeichert werden'); setSavingFollowUp(false); return; }
      if (leadId && tarif) {
        // v5: Beim Speichern entsteht das Angebot mit Vertragswert — die
        // Wiedervorlage ist das Follow-up-Datum (R4: ohne Datum kein Angebot).
        // Pauschale = ein Vier-Wochen-Beitrag (52 = 13x4 Wochen, siehe tarife.js).
        try {
          await fetch(`${API}/api/vertrieb/${leadId}/angebot`, {
            method: 'POST', headers: getHeaders(), credentials: 'include',
            body: JSON.stringify({
              plan_id: pid || tarif.id, plan_name: tarif.name, laufzeit,
              monatsbeitrag: vierWochen,
              einstieg: einstiegSatz.regulaer,
              pauschale: pauschaleGilt ? vierWochen : 0,
              zweite_einheit: famZuschlag > 0,
              gym_sauna: gymSaunaAktiv || gymSaunaInklusive,
              geschwister: geschwisterAktiv,
              wiedervorlage_am: `${followUp.date}T${followUp.time || '10:00'}:00`,
              notiz: leadNotes || '',
            }),
          });
        } catch { /* Angebot ist Zusatz — das Follow-up gilt trotzdem */ }
      }
      if (leadId) {
        await fetch(`${API}/api/follow-ups`, {
          method: 'POST', headers: getHeaders(), credentials: 'include',
          body: JSON.stringify({
            lead_id: leadId,
            due_date: `${followUp.date}T${followUp.time || '10:00'}:00`,
            channel: followUp.channel,
            subject: followUp.subject || `Nachfassen: ${tarif?.name || 'Tarif'}`,
            notes: followUp.notes, priority: followUp.priority, assigned_to: user?.name || '',
          }),
        });
      }
      toast.success(leadData?.vorhanden ? 'Follow-up am vorhandenen Lead gespeichert' : 'Lead & Follow-up gespeichert!');
      setResult({ followUpSaved: true, leadName, vorhanden: !!leadData?.vorhanden, planName: tarif?.name, planDuration: durationLabel });
    } catch { toast.error('Fehler beim Speichern'); }
    setSavingFollowUp(false);
  };

  const resetAll = () => {
    setStep(1); setResult(null); setSelectedTarifId(null); setLaufzeit(EMPFOHLENE_LAUFZEIT); setShowAlternatives(false);
    setZielgruppe('erwachsener'); setFrequenz('2x'); setGymSauna(false); setVorerfahrung('nein'); setSelectedGoals([]);
    setLeadName(''); setLeadPhone(''); setLeadEmail(''); setStartDate(new Date().toISOString().split('T')[0]);
    setLeadPriority('mittel'); setLeadNotes(''); setCheckup('none'); setTag1Abschluss(true); setVorauszahler(false); setContractType('erwachsene');
    setIsFollowUpOnly(false); setSavingFollowUp(false); setSchrittGespeichert(false); setVorb(null);
    setForm({ vorname: '', nachname: '', geburtsdatum: '', strasse: '', plz: '', ort: '', telefon: '', email: '',
      kontoinhaber: '', iban: '', bic: '', kreditinstitut: '', vertragsbeginn: new Date().toISOString().split('T')[0],
      notizen: '', erziehungsberechtigter_name: '', erziehungsberechtigter_telefon: '', erziehungsberechtigter_email: '',
      agb_akzeptiert: false, sepa_akzeptiert: false, ziel_satz: '', eltern_zustimmung: false,
      unterschrift_mitglied: null, unterschrift_eltern: null, abschluss_ort: 'studio', widerruf_akzeptiert: false, einwilligungen_client: {} });
    setErklaerungHaken({}); setErklaerungOffen(null); setPt({ wahl: '', am: '' }); setPtKandidaten([]);
    // Sonst erbt der nächste Kunde Werber und Rabatt-Schalter des vorigen — der Werber bekäme die Prämie doppelt (03.10.2026)
    setWerber(null); setGeschwister(false); setFamKind2(false); setFamErw2(false); setIndiv(false); setIndivBeitrag(''); setIndivGrund('');
    setFollowUp({ date: '', time: '10:00', channel: 'telefon', subject: '', notes: '', priority: 'mittel' });
  };

  // Alternative Tarife (gleiche Linie) für "Weitere Optionen"
  const alternativen = tarif ? TARIFE.filter(t => t.linie === tarif.linie && t.id !== tarif.id) : [];

  return (
    <div className="space-y-6" data-testid="verkauf-page" ref={topRef}>
      {/* Kopf + interne Referenz */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Verkaufsrechner</h1>
          <p className="text-xs text-muted-foreground">Wert vor Preis — Bedarf, Empfehlung, Abschluss</p>
        </div>
        <Link to="/verkauf/referenz" className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="link-referenz">
          Interne Tarifübersicht
        </Link>
      </div>
      {!vb('umvertragung') && (
        <div className="inline-flex rounded-full border border-border p-0.5 text-sm" role="tablist" data-testid="verkauf-modus">
          {[['vertrag', 'Neuer Vertrag'], ['upsell', 'Upsell ohne neuen Vertrag']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={modus === k} onClick={() => setModus(k)}
              className={`rounded-full px-4 h-8 font-medium ${modus === k ? 'bg-marke text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`verkauf-modus-${k}`}>{l}</button>
          ))}
          {/* Geschwister (26.10.2026): mehrere Kinder in einem Vertrag oder Geschwisterkind dazubuchen — eigene Seite */}
          <Link to="/verkauf/geschwister" className="rounded-full px-4 h-8 font-medium inline-flex items-center text-muted-foreground hover:text-foreground" data-testid="verkauf-modus-geschwister">Geschwister</Link>
        </div>
      )}
      {modus === 'upsell' ? <UpsellVerkauf /> : (<>
      {vb('umvertragung') && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm" data-testid="umvertragung-hinweis">
          <b>Neue Laufzeit für {vb('name') || 'dieses Mitglied'}.</b> Der Abschluss hier ersetzt den laufenden Vertrag: neuer Tarif und neue Laufzeit ab dem gewählten Vertragsbeginn, der alte Vertrag endet zu diesem Tag, das SEPA-Mandat bleibt. Unterschrift und Erklärungen wie beim Neuvertrag, Pauschale im ersten Jahr erlassen.
        </div>
      )}

      {/* Fortschritts-Leiste */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="step-indicator">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-center flex-shrink-0">
            <button
              onClick={() => s.n <= step && goStep(s.n)}
              disabled={s.n > step}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                s.n === step ? 'bg-marke text-white'
                  : s.n < step ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}
              data-testid={`step-btn-${s.n}`}
            >
              {s.n < step ? <Check className="w-3.5 h-3.5" /> : <span>{s.n}</span>}
              {s.label}
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 mx-0.5" />}
          </div>
        ))}
      </div>

      {/* ══════════ SCHRITT 1: Bedarf erfassen ══════════ */}
      {step === 1 && (
        <div className="max-w-3xl space-y-5" data-testid="step-1-bedarf">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Bedarf erfassen</h2>
            <p className="text-sm text-muted-foreground mt-1">Ein paar Fragen — daraus leiten wir die passende Empfehlung ab.</p>
          </div>

          {/* Zielgruppe */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">Für wen ist das Training?</h3>
            <div className="grid grid-cols-3 gap-2">
              {ZIELGRUPPEN.map(z => (
                <button key={z.id} onClick={() => setZielgruppe(z.id)} data-testid={`zg-${z.id}`}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-xs font-bold transition-all ${zielgruppe === z.id ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                  <z.icon className="w-5 h-5" /> {z.label}
                </button>
              ))}
            </div>
          </div>

          {/* Frequenz + Gym/Sauna */}
          {zielgruppe !== 'familie' && (
            <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
              <h3 className="text-sm font-bold">Wie oft Training / Woche?</h3>
              <div className="grid grid-cols-3 gap-2">
                {FREQUENZEN.map(f => (
                  <button key={f.id} onClick={() => setFrequenz(f.id)} data-testid={`freq-${f.id}`}
                    className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${frequenz === f.id ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">Zusatzwünsche</h3>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="gym-sauna">
              <input type="checkbox" checked={gymSauna} onChange={e => setGymSauna(e.target.checked)} className="w-4 h-4 accent-marke" />
              <span className="text-sm">Freies Gym-Training & Sauna gewünscht</span>
            </label>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground block mb-1">Vorerfahrung Kampfsport?</label>
              <select value={vorerfahrung} onChange={e => setVorerfahrung(e.target.value)} data-testid="vorerfahrung"
                className="w-full h-10 px-3 bg-white border border-border/60 rounded-lg text-sm">
                <option value="nein">Keine / Anfänger</option>
                <option value="etwas">Etwas Erfahrung</option>
                <option value="viel">Fortgeschritten</option>
              </select>
            </div>
          </div>

          {/* Ziele / Motiv */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">Ziel / Motiv</h3>
            <div className="flex flex-wrap gap-2">
              {GOALS.map(g => {
                const on = selectedGoals.includes(g.id);
                return (
                  <button key={g.id} onClick={() => toggleGoal(g.id)} data-testid={`goal-${g.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-medium transition-all ${on ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                    <g.icon className="w-3.5 h-3.5" /> {g.label}
                  </button>
                );
              })}
            </div>
            <textarea value={leadNotes} onChange={e => setLeadNotes(e.target.value)} rows={2} placeholder="Freitext: Was ist dem Interessenten wichtig?" data-testid="motiv-notes"
              className="w-full px-3 py-2 bg-white border border-border/60 rounded-lg text-sm resize-none" />
          </div>

          {/* Kontaktdaten */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">Kontaktdaten</h3>
            <div className="grid grid-cols-2 gap-3">
              <VInput label="Name *" v={leadName} set={setLeadName} tid="lead-name" span2 />
              <VInput label="Telefon *" v={leadPhone} set={setLeadPhone} tid="lead-phone" />
              <VInput label="E-Mail" type="email" v={leadEmail} set={setLeadEmail} tid="lead-email" />
              <VInput label="Wunsch-Startdatum" type="date" v={startDate} set={setStartDate} tid="lead-start" />
              <div>
                <label className="text-[11px] font-medium text-muted-foreground block mb-1">Priorität</label>
                <select value={leadPriority} onChange={e => setLeadPriority(e.target.value)} data-testid="lead-priority"
                  className="w-full h-10 px-3 bg-white border border-border/60 rounded-lg text-sm">
                  {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <button onClick={goToEmpfehlung} data-testid="btn-to-empfehlung"
            className="w-full flex items-center justify-center gap-2 bg-marke text-white text-base font-bold py-4 rounded-xl hover:bg-marke-dunkel transition-colors shadow-lg">
            Weiter zur Empfehlung <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ══════════ SCHRITT 2: Empfehlung ══════════ */}
      {step === 2 && tarif && (
        <div className="max-w-2xl space-y-5" data-testid="step-2-empfehlung">
          <button onClick={() => goStep(1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="btn-back-2">
            <ArrowLeft className="w-4 h-4" /> Zurück zum Bedarf
          </button>
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-marke/10 text-marke text-[11px] font-bold mb-2">
              <Sparkles className="w-3.5 h-3.5" /> {tarif.id === empfohlenerTarifId ? 'Unsere Empfehlung' : 'Weitere Option'}
            </div>
            <h2 className="text-2xl font-bold tracking-tight">{tarif.name}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{tarif.einheiten} · {LINIE_LABEL[tarif.linie]}</p>
          </div>

          {/* Karte: erst Leistungen, DANN Preis */}
          <div className="bg-white rounded-2xl border-2 border-marke/30 p-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              {tarif.empfohlen && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-marke text-white text-[11px] font-bold">
                  <Star className="w-3.5 h-3.5 fill-current" /> Bestes Preis-Leistungs-Verhältnis
                </div>
              )}
              {tarif.linie === 'family' && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-[11px] font-bold">
                  <Gift className="w-3.5 h-3.5" /> ab 12 Mon. inkl. monatl. P1 Check-Up (Wert 45 €)
                </div>
              )}
            </div>
            <ul className="space-y-2">
              {tarif.leistungen.map((l, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> <span>{l}</span>
                </li>
              ))}
            </ul>

            {/* Laufzeit-Wahl */}
            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Laufzeit</div>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${laufzeitenFuer(tarif).length}, 1fr)` }}>
                {laufzeitenFuer(tarif).map(lz => {
                  const on = laufzeit === lz;
                  const best = lz === EMPFOHLENE_LAUFZEIT;
                  return (
                    <button key={lz} onClick={() => setLaufzeit(lz)} data-testid={`laufzeit-${lz}`}
                      className={`relative py-2 rounded-xl border-2 text-xs font-bold transition-all ${on ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                      {best && <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-marke text-white text-[8px] font-bold whitespace-nowrap">Best of</span>}
                      {fmt(tarif.wochenpreis[lz])} €<span className="block text-[9px] font-normal opacity-70">{LAUFZEIT_LABEL[lz]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Family: zweite Einheit je Person */}
            {istFamily && (
              <div>
                <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Zweite Einheit dazubuchen</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setFamKind2(v => !v)} data-testid="fam-kind2"
                    className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${famKind2 ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                    Kind 2×/Woche<span className="block text-[9px] font-normal opacity-70">+{ZWEITE_EINHEIT.kinder.toFixed(2).replace('.', ',')} €/Wo · +{ZWEITE_EINHEIT.kinder * 4} €/Monat</span>
                  </button>
                  <button onClick={() => setFamErw2(v => !v)} data-testid="fam-erw2"
                    className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${famErw2 ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                    Erwachsener 2×/Woche<span className="block text-[9px] font-normal opacity-70">+{ZWEITE_EINHEIT.erwachsene.toFixed(2).replace('.', ',')} €/Wo · +{ZWEITE_EINHEIT.erwachsene * 4} €/Monat</span>
                  </button>
                </div>
              </div>
            )}

            {/* Geschwisterrabatt — erscheint nur, wo er laut Preisliste gilt */}
            {geschwisterMoeglich && (
              <button onClick={() => setGeschwister(v => !v)} data-testid="geschwister"
                className={`w-full py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${geschwisterAktiv ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                Geschwisterkind (−{Math.round(GESCHWISTER.rabatt * 100)} %)
                <span className="block text-[9px] font-normal opacity-70">Nur bei 24 Monaten · gilt fürs günstigere Kind</span>
              </button>
            )}

            {/* Individuelle Vereinbarung (alle Tarife, 26.10.2026): freier Monatsbeitrag mit Grund statt Listenpreis */}
            {indivMoeglich && (
              <div className="space-y-2" data-testid="individuell">
                <button onClick={() => setIndiv(v => !v)} data-testid="individuell-toggle"
                  className={`w-full py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${indiv ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                  Individuelle Vereinbarung
                  <span className="block text-[9px] font-normal opacity-70">Freier Monatsbeitrag statt Listenpreis · ersetzt Geschwister- und Vorauszahlerrabatt</span>
                </button>
                {indiv && (
                  <div className="grid grid-cols-2 gap-2">
                    <input inputMode="decimal" value={indivBeitrag} onChange={e => setIndivBeitrag(e.target.value)} placeholder="Monatsbeitrag €"
                      className="h-9 rounded-lg border border-border/60 px-3 text-sm" data-testid="individuell-beitrag" />
                    <input value={indivGrund} onChange={e => setIndivGrund(e.target.value)} placeholder="Grund (Pflicht)"
                      className="h-9 rounded-lg border border-border/60 px-3 text-sm" data-testid="individuell-grund" />
                  </div>
                )}
              </div>
            )}

            {/* Preis — Wochenpreis dominant */}
            <div className="pt-3 border-t border-border/50">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-marke leading-none">{fmt(wpreis)} €</span>
                <span className="text-lg font-semibold text-muted-foreground">/ Woche</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1.5">entspricht {fmt(vierWochen)} € alle 4 Wochen{bindungMonate > 0 ? ` · ${bindungMonate} Mon. Laufzeit` : ' · ohne Bindung'}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{ohneBausteine ? 'Sondertarif: kein Betreuter Einstieg, keine Ferien- & Event-Pauschale, keine Rabatte · nach 6 Monaten monatlich kündbar' : `zzgl. einmalig Betreuter Einstieg ${einstiegSatz.regulaer} € (am Beratungstag nur ${einstiegSatz.tag1} €)`}</div>
            </div>
          </div>

          {/* Weitere Optionen */}
          <div>
            <button onClick={() => setShowAlternatives(v => !v)} data-testid="btn-alternatives"
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ChevronDown className={`w-4 h-4 transition-transform ${showAlternatives ? 'rotate-180' : ''}`} /> Weitere Optionen anzeigen
            </button>
            {showAlternatives && (
              <div className="mt-3 grid gap-2">
                {alternativen.map(alt => {
                  const altPreis = alt.wochenpreis[laufzeit] ?? alt.wochenpreis.m12 ?? laufzeitenFuer(alt).map(lz => alt.wochenpreis[lz])[0] ?? 0;
                  const guenstiger = altPreis < wpreis;
                  return (
                    <button key={alt.id} data-testid={`alt-${alt.id}`}
                      onClick={() => { setSelectedTarifId(alt.id); if (alt.wochenpreis[laufzeit] == null) setLaufzeit(alt.wochenpreis.m12 != null ? 'm12' : laufzeitenFuer(alt)[0]); }}
                      className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-white hover:border-marke/40 text-left transition-colors">
                      <div>
                        <div className="text-sm font-bold">{alt.name}</div>
                        <div className="text-[11px] text-muted-foreground">{alt.einheiten} · {guenstiger ? 'kleinere Alternative' : 'größere Option'}</div>
                      </div>
                      <div className="text-sm font-bold">{fmt(altPreis)} €<span className="text-[10px] font-normal text-muted-foreground"> / Wo.</span></div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={() => goStep(3)} data-testid="btn-to-tag1"
            className="w-full flex items-center justify-center gap-2 bg-marke text-white text-base font-bold py-4 rounded-xl hover:bg-marke-dunkel transition-colors shadow-lg">
            Weiter zum Tag-1-Angebot <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ══════════ SCHRITT 3: Tag 1 Angebot ══════════ */}
      {step === 3 && tarif && (
        <div className="max-w-2xl space-y-5" data-testid="step-3-tag1">
          <button onClick={() => goStep(2)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="btn-back-3">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Empfehlung
          </button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Tag 1 Angebot</h2>
            <p className="text-sm text-muted-foreground mt-1">{tarif.name} · {durationLabel} · {fmt(wpreis)} €/Woche</p>
          </div>

          {/* Betreuter Einstieg 199 -> 99 */}
          <div className="bg-gradient-to-br from-marke/5 to-transparent border-2 border-marke/30 rounded-2xl p-5 flex items-center justify-between">
            <div>
              <div className="text-sm font-bold">Betreuter Einstieg</div>
              <p className="text-xs text-muted-foreground mt-0.5">Individuelle Einweisung & Startbegleitung — nur am heutigen Beratungstag</p>
            </div>
            <div className="text-right">
              {ohneBausteine ? (
                <div className="text-sm font-bold text-emerald-600" data-testid="einstieg-entfaellt">entfällt beim Sondertarif</div>
              ) : (<>
                <div className="text-xs text-muted-foreground line-through">{einstiegSatz.regulaer} €</div>
                <div className="text-2xl font-extrabold text-marke">{einstiegSatz.tag1} €</div>
                <div className="text-[10px] font-bold text-emerald-600">{einstiegSatz.regulaer - einstiegSatz.tag1} € gespart</div>
              </>)}
            </div>
          </div>

          {/* Check-Up Add-on */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-marke" /> P1 Check-Up (optional)</h3>
            {checkupInklusive && (
              <div className="text-[11px] text-purple-700 bg-purple-50 rounded-lg px-3 py-1.5">In dieser Laufzeit sind {checkupKontingent} Standard-Check-Ups inklusive (Wert {checkupKontingent * CHECKUP.standard.preis} €){tarif?.linie === 'family' ? ' – frei auf beide Personen verteilbar' : ''}.</div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'none', title: 'Keiner', sub: '' },
                { id: 'standard', title: 'Standard', sub: `${CHECKUP.standard.minuten} Min · ${checkupInklusive ? 'inkl.' : CHECKUP.standard.preis + ' €'}` },
                { id: 'intensiv', title: 'Intensiv', sub: `${CHECKUP.intensiv.minuten} Min · ${CHECKUP.intensiv.preis} €` },
              ].map(o => (
                <button key={o.id} onClick={() => setCheckup(o.id)} data-testid={`checkup-${o.id}`}
                  className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${checkup === o.id ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                  {o.title}<span className="block text-[9px] font-normal opacity-70">{o.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Gym & Sauna Add-on */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-marke" /> Gym & Sauna (optional)</h3>
            {gymSaunaInklusive ? (
              <div className="text-[11px] text-purple-700 bg-purple-50 rounded-lg px-3 py-1.5">In Performance Plus bereits enthalten.</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {[{ id: false, title: 'Ohne', sub: '' },
                  { id: true, title: 'Dazu buchen', sub: `+${GYM_SAUNA.wochenpreis.toFixed(2).replace('.', ',')} €/Wo · ${GYM_SAUNA.wochenpreis * 4} €/Monat` }].map(o => (
                  <button key={String(o.id)} onClick={() => setGymSauna(o.id)} data-testid={`gymsauna-${o.id}`}
                    className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${gymSauna === o.id ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}>
                    {o.title}<span className="block text-[9px] font-normal opacity-70">{o.sub}</span>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">Gym-Zugang 6–22 Uhr + {GYM_SAUNA.saunaCreditsProMonat} Sauna-Credits/Monat · monatlich kündbar · nur für Mitglieder</p>
          </div>

          {/* Zusammenfassung */}
          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Tarif</span><span className="font-medium">{tarif.name} ({durationLabel})</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Beitrag</span><span className="font-bold">{fmt(wpreis)} €/Woche · {fmt(vierWochen)} €/4 Wochen</span></div>
            {famZuschlag > 0 && <div className="flex justify-between"><span className="text-muted-foreground">darin: zweite Einheit ({[famKind2 && 'Kind', famErw2 && 'Erwachsener'].filter(Boolean).join(' + ')})</span><span className="font-medium">+{fmt(famZuschlag)} €/Woche</span></div>}
            {pauschaleGilt ? (
              <div className="flex justify-between gap-3"><span className="text-muted-foreground">zzgl. 1× jährlich Ferien- & Event-Pauschale (= 1 Monatsbeitrag, fällig {FERIEN_EVENT_PAUSCHALE.faelligAm})</span><span className="font-medium text-right">{fmt(vierWochen)} € · <span className="font-normal text-muted-foreground">{pauschaleText}</span></span></div>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">{ohneBausteine ? 'Sondertarif' : 'Ohne Bindung'}</span><span className="font-medium text-muted-foreground">{ohneBausteine ? 'Keine Pauschale · Camps & Seminare kostenpflichtig' : 'Camps & Seminare kostenpflichtig'}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Betreuter Einstieg{tag1Abschluss ? ' (Tag 1)' : ''}</span><span className="font-medium">{einstiegPreis} € einmalig</span></div>
            {/* Tag-1-Preis: der Server prüft ihn (nur am Tag des Probetrainings oder mit laufendem Angebot) */}
            {!ohneBausteine && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1" data-testid="tag1-wahl">
                <input type="checkbox" className="accent-marke" checked={tag1Abschluss} onChange={e => setTag1Abschluss(e.target.checked)} />
                Tag-1-Preis anwenden
                {vorb?.tag1_angebot?.bis && <span className={vorb.tag1_gilt ? 'text-violet-700 font-medium' : 'text-amber-700'} data-testid="tag1-angebot-info">
                  · Tag-1-Angebot {vorb.tag1_gilt ? 'gilt noch' : 'ist abgelaufen'} bis {vorb.tag1_angebot.bis.slice(8, 10)}.{vorb.tag1_angebot.bis.slice(5, 7)}.</span>}
              </label>
            )}
            {checkup !== 'none' && <div className="flex justify-between"><span className="text-muted-foreground">Check-Up ({checkup})</span><span className="font-medium">{checkupPreis === 0 ? `inklusive (${checkupKontingent}× in der Laufzeit)` : `${fmt(checkupPreis)} €`}</span></div>}
            {geschwisterAktiv && <div className="flex justify-between"><span className="text-muted-foreground">Geschwisterrabatt (−{Math.round(GESCHWISTER.rabatt * 100)} %)</span><span className="font-medium text-emerald-600">−{fmt(geschwisterAbzug)} €/Woche</span></div>}
            {gymSaunaAktiv && <div className="flex justify-between"><span className="text-muted-foreground">Gym & Sauna Add-on</span><span className="font-medium">+{fmt(GYM_SAUNA.wochenpreis)} €/Woche · +{fmt(GYM_SAUNA.wochenpreis * 4)} €/4 Wochen</span></div>}
            {gymSaunaInklusive && <div className="flex justify-between"><span className="text-muted-foreground">Gym & Sauna</span><span className="font-medium">inklusive</span></div>}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => { prefillContract(); goStep(4); }} data-testid="btn-abschliessen"
              className="flex-1 flex items-center justify-center gap-2 bg-marke text-white text-base font-bold py-4 rounded-xl hover:bg-marke-dunkel transition-colors shadow-lg">
              <FileSignature className="w-5 h-5" /> Sofort abschließen
            </button>
            <button onClick={() => { setTag1Abschluss(false); setIsFollowUpOnly(true); goStep(5); }} data-testid="btn-followup-plan"
              className="flex-1 flex items-center justify-center gap-2 border-2 border-border/60 text-muted-foreground font-bold py-4 rounded-xl hover:bg-muted/20 transition-colors">
              <Clock className="w-5 h-5" /> Follow-up planen
            </button>
          </div>

          <button onClick={empfehlungPdf} disabled={pdfLaeuft} data-testid="btn-empfehlung-pdf"
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-muted-foreground py-3 rounded-xl border border-border/60 hover:bg-muted/20 transition-colors disabled:opacity-50">
            {pdfLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Empfehlung zum Mitgeben (PDF)
          </button>
        </div>
      )}

      {/* ══════════ SCHRITT 4: Vertragsabschluss ══════════ */}
      {step === 4 && tarif && (
        <div className="max-w-3xl space-y-5" data-testid="step-4-vertrag">
          <button onClick={() => goStep(3)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="btn-back-4">
            <ArrowLeft className="w-4 h-4" /> Zurück zum Angebot
          </button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Vertragsabschluss</h2>
            <p className="text-sm text-muted-foreground mt-1">{tarif.name} · {durationLabel} · {fmt(wpreis)} €/Woche = {fmt(vierWochen)} €/4 Wochen</p>
          </div>

          <div className="flex gap-2" data-testid="contract-type">
            <button onClick={() => setContractType('erwachsene')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${!isKidsLike ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground'}`} data-testid="type-adult">
              <User className="w-4 h-4" /> Erwachsene
            </button>
            <button onClick={() => setContractType(tarif.linie === 'family' ? 'family' : 'kinder')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${isKidsLike ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-border/60 text-muted-foreground'}`} data-testid="type-youth">
              <Baby className="w-4 h-4" /> Kind / Jugend {tarif.linie === 'family' ? '(Family)' : ''}
            </button>
            {/* Geschwister (26.10.2026): mehrere Kinder in EINEM Vertrag — eigene Seite */}
            {isKidsLike && tarif.linie === 'kinder' && (
              <Link to="/verkauf/geschwister" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-border/60 text-xs font-bold text-muted-foreground hover:text-foreground" data-testid="type-geschwister">
                <Users className="w-4 h-4" /> Geschwister in einem Vertrag
              </Link>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-4">
            <h3 className="text-sm font-bold">{isKidsLike ? 'Mitglied (Kind)' : 'Persönliche Daten'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <VInput label="Vorname *" v={form.vorname} set={v => updateForm('vorname', v)} tid="v-vorname" />
              <VInput label="Nachname *" v={form.nachname} set={v => updateForm('nachname', v)} tid="v-nachname" />
              <VInput label="Geburtsdatum" type="date" v={form.geburtsdatum} set={v => updateForm('geburtsdatum', v)} tid="v-geb" />
              <VInput label="Telefon *" v={form.telefon} set={v => updateForm('telefon', v)} tid="v-tel" />
              <VInput label="E-Mail *" type="email" v={form.email} set={v => updateForm('email', v)} tid="v-email" span2 />
              <VInput label="Straße, Hausnr." v={form.strasse} set={v => updateForm('strasse', v)} tid="v-str" />
              <div className="grid grid-cols-3 gap-2">
                <VInput label="PLZ" v={form.plz} set={v => updateForm('plz', v)} tid="v-plz" />
                <div className="col-span-2"><VInput label="Ort" v={form.ort} set={v => updateForm('ort', v)} tid="v-ort" /></div>
                <div className="col-span-3"><OrtVorschlaege orte={ortListe} aktuell={form.ort} wahl={v => updateForm('ort', v)} /></div>
              </div>
            </div>
          </div>

          {isKidsLike && (
            <div className="bg-white rounded-2xl border border-emerald-200 p-5 space-y-4">
              <h3 className="text-sm font-bold text-emerald-700">Erziehungsberechtigte/r</h3>
              <div className="grid grid-cols-2 gap-3">
                <VInput label="Name, Vorname *" v={form.erziehungsberechtigter_name} set={v => updateForm('erziehungsberechtigter_name', v)} tid="v-gn" />
                <VInput label="Telefon" v={form.erziehungsberechtigter_telefon} set={v => updateForm('erziehungsberechtigter_telefon', v)} tid="v-gt" />
                <VInput label="E-Mail" type="email" v={form.erziehungsberechtigter_email} set={v => updateForm('erziehungsberechtigter_email', v)} tid="v-ge" span2 />
              </div>
              {eigenesKontoMoeglich && (
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="v-eigenes-konto">
                  <input type="checkbox" className="mt-0.5" checked={!!form.eigenes_konto} onChange={(e) => updateForm('eigenes_konto', e.target.checked)} />
                  <span>
                    <span className="font-semibold">Bucht selbst Kurse (eigenes App-Konto, ab 16)</span>
                    <span className="block text-muted-foreground">Meldet sich mit der eigenen E-Mail oben an, kein Elternkonto. Die E-Mail darf nicht die der Eltern sein.</span>
                  </span>
                </label>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">Vertragsdaten</h3>
            <div className="grid grid-cols-2 gap-3">
              <VInput label="Vertragsbeginn *" type="date" v={form.vertragsbeginn} set={v => updateForm('vertragsbeginn', v)} tid="v-beginn" />
              <div><label className="text-[11px] font-medium text-muted-foreground block mb-1">Laufzeit</label><div className="h-10 px-3 flex items-center bg-gray-50 rounded-lg text-sm font-medium border border-border/60">{durationLabel}</div></div>
            </div>
          </div>

          {vzInfo && (
            <button type="button" onClick={() => setVorauszahler(v => !v)} data-testid="vorauszahler-toggle"
              className={`w-full text-left rounded-2xl border-2 p-5 transition-all ${vorauszahler ? 'border-emerald-500 bg-emerald-50' : 'border-border/60 bg-white hover:border-border'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <input type="checkbox" readOnly checked={vorauszahler} className="w-5 h-5 accent-emerald-600" />
                  <div>
                    <div className="text-sm font-bold">Vorauszahler – {vzInfo.prozent} % sparen</div>
                    <div className="text-xs text-muted-foreground">Gesamte Laufzeit einmalig zahlen statt {fmt(wpreis)} €/Woche per Lastschrift</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[11px] text-muted-foreground line-through">{fmt(vzInfo.gesamt)} €</div>
                  <div className="text-xl font-extrabold text-emerald-600">{fmt(vzInfo.betrag)} €</div>
                  <div className="text-[10px] font-bold text-emerald-600">− {fmt(vzInfo.ersparnis)} € gespart</div>
                </div>
              </div>
            </button>
          )}

          <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-3">
            <h3 className="text-sm font-bold">SEPA-Lastschriftmandat {vorauszahler && <span className="text-[10px] font-normal text-muted-foreground">(optional bei Vorauszahlung)</span>}</h3>
            <p className="text-[10px] text-muted-foreground">Gläubiger-ID: DE73ZZZ00002764610</p>
            <div className="grid grid-cols-2 gap-3">
              <VInput label="Kontoinhaber/in *" v={form.kontoinhaber} set={v => updateForm('kontoinhaber', v)} tid="v-kto" span2 />
              <VInput label="IBAN *" v={form.iban} set={v => updateForm('iban', v)} tid="v-iban" ph="DE00 0000 0000 0000 0000 00" span2 />
              <VInput label="BIC" v={form.bic} set={v => updateForm('bic', v)} tid="v-bic" />
              <VInput label="Kreditinstitut" v={form.kreditinstitut} set={v => updateForm('kreditinstitut', v)} tid="v-bank" />
              <div className="col-span-2"><BankHinweis bank={bankErkannt} /></div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-1" data-testid="ziel-satz-feld">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ziel in einem Satz (Pflicht)</label>
              <textarea value={form.ziel_satz} onChange={e => updateForm('ziel_satz', e.target.value)} rows={2} data-testid="v-ziel-satz"
                placeholder="Was will das Mitglied erreichen? — daraus entstehen die Sprechtexte für Tag 0, Tag 2 und Tag 14"
                className="w-full px-3 py-2 bg-white border border-border/60 rounded-lg text-sm resize-none" />
            </div>
            <div className="space-y-1.5" data-testid="probetraining-vorher">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Probetraining vorher? (Pflicht)</label>
              <div className="flex flex-wrap gap-1.5">
                {ptKandidaten.filter(k => k.status !== 'nicht_erschienen').map(k => {
                  const wert = k.art === 'buchung' ? `b:${k.buchung_id}` : `l:${k.lead_id}`;
                  return (
                    <button key={wert} type="button" onClick={() => setPt({ wahl: wert, am: '' })} data-testid="pt-kandidat"
                      className={`rounded-full border px-3 py-1.5 text-xs ${pt.wahl === wert ? 'border-marke bg-marke/10 font-semibold' : 'border-border hover:bg-muted'}`}>
                      Ja: {k.name} am {k.am.split('-').reverse().join('.')}{k.status === 'geplant' ? ' (gebucht)' : ''}
                    </button>
                  );
                })}
                <button type="button" onClick={() => setPt({ wahl: 'datum', am: pt.am })} data-testid="pt-datum"
                  className={`rounded-full border px-3 py-1.5 text-xs ${pt.wahl === 'datum' ? 'border-marke bg-marke/10 font-semibold' : 'border-border hover:bg-muted'}`}>
                  {ptKandidaten.length ? 'Ja, an einem anderen Tag' : 'Ja, am …'}
                </button>
                <button type="button" onClick={() => setPt({ wahl: 'ohne', am: '' })} data-testid="pt-ohne"
                  className={`rounded-full border px-3 py-1.5 text-xs ${pt.wahl === 'ohne' ? 'border-marke bg-marke/10 font-semibold' : 'border-border hover:bg-muted'}`}>
                  Nein, direkt abgeschlossen
                </button>
              </div>
              {pt.wahl === 'datum' && (
                <input type="date" value={pt.am} max={new Date().toISOString().split('T')[0]} onChange={e => setPt({ wahl: 'datum', am: e.target.value })}
                  className="h-9 px-3 bg-white border border-border/60 rounded-lg text-sm" data-testid="pt-am" />
              )}
              <p className="text-[11px] text-muted-foreground">Zählt für die Abschlussquote. Gefunden werden Probetrainings der letzten 45 Tage mit derselben Nummer, E-Mail oder demselben Nachnamen.</p>
            </div>
            <div className="space-y-1.5" data-testid="geworben-von">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Geworben von einem Mitglied? (optional)</label>
              <MitgliedSuche wert={werber} onWahl={setWerber} placeholder="Name des werbenden Mitglieds…" tid="v-werber" />
              <p className="text-[11px] text-muted-foreground">Das Mitglied bekommt dafür Prämienpunkte, in einer Empfehlungsaktion mehr als sonst.</p>
            </div>
            {/* Unterschrift-Station (03.10.2026): Vertrag am iMac fertig, Unterschrift am Theken-iPad oder per QR am Handy */}
            <div className="rounded-xl border border-border/60 bg-white p-4 space-y-2" data-testid="unterschrift-senden-box">
              <p className="text-sm font-semibold">Unterschrift am iPad an der Theke</p>
              <p className="text-xs text-muted-foreground">Der Kunde liest den Vertrag auf dem iPad, setzt dort die Bestätigungen und unterschreibt. Alternativ scannt er den QR-Code mit dem eigenen Handy. Der Abschluss läuft danach wie gewohnt.</p>
              <button type="button" onClick={sendeZurUnterschrift} disabled={uvSendet || submitting}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold py-3 rounded-lg border-2 border-marke text-marke hover:bg-marke/5 transition-colors disabled:opacity-50"
                data-testid="btn-zur-unterschrift">
                {uvSendet ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet …</> : <><FileSignature className="w-4 h-4" /> Zur Unterschrift senden</>}
              </button>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">Oder direkt an diesem Gerät unterschreiben</p>
            {/* Abschluss immer im Studio (Betreiber 21.09.2026: kein Fernabschluss) — das Backend kennt abschluss_ort=fern weiter, die Oberflaeche bietet es nicht an. */}
            <Consent checked={form.sepa_akzeptiert} onChange={haken('sepa_akzeptiert', 'sepa')} tid="cb-sepa" text="Ich ermächtige die P1 Performance Center GmbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die Lastschriften einzulösen. Ich kann innerhalb von acht Wochen ab Belastung die Erstattung verlangen." />
            <Consent checked={form.agb_akzeptiert} onChange={haken('agb_akzeptiert', 'agb')} tid="cb-agb" text="Ich habe die AGB und Datenschutzerklärung gelesen und akzeptiere diese." />
            {isKidsLike && <Consent checked={form.eltern_zustimmung} onChange={haken('eltern_zustimmung', 'eltern')} tid="cb-parent" text="Als Erziehungsberechtigte/r stimme ich dem Vertragsabschluss für mein Kind zu." />}
            {erklaerungenFehler && erklaerungen.length === 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm flex flex-wrap items-center gap-3" data-testid="erklaerungen-fehler">
                <span className="flex-1">Die Pflicht-Erklärungen (Haftung, Hausordnung, Datenschutz) konnten nicht geladen werden — ohne sie lässt sich der Vertrag nicht abschließen.</span>
                <button type="button" className="px-3 py-1.5 rounded-md bg-marke text-white text-sm font-medium" onClick={ladeErklaerungen}>Erklärungen erneut laden</button>
              </div>
            )}
            {erklaerungen.length > 0 && (
              <div className="space-y-2" data-testid="erklaerungen">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">Erklärungen{isKidsLike ? ' (durch Erziehungsberechtigte/n)' : ''}</p>
                {erklaerungen.map(e => (
                  <div key={e.type} className="bg-white rounded-xl border border-border/60">
                    <label className="flex items-start gap-3 cursor-pointer p-4">
                      <input type="checkbox" checked={!!erklaerungHaken[e.type]} onChange={ev => setErklaerungHaken(h => ({ ...h, [e.type]: ev.target.checked }))} className="accent-marke mt-0.5 w-4 h-4" data-testid={`cb-${e.type}`} />
                      <span className="text-xs text-muted-foreground flex-1">
                        <span className="font-medium text-foreground">{e.title}</span>{e.pflicht ? '' : ' (freiwillig)'} — gelesen und {e.type === 'foto_video' ? 'eingewilligt' : 'akzeptiert'}{e.version ? ` · Version ${e.version}` : ''}
                      </span>
                      <button type="button" className="text-xs font-medium hover:underline text-marke flex-shrink-0" onClick={ev => { ev.preventDefault(); setErklaerungOffen(o => o === e.type ? null : e.type); }}>{erklaerungOffen === e.type ? 'Text schließen' : 'Text anzeigen'}</button>
                    </label>
                    {erklaerungOffen === e.type && (
                      <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-muted-foreground px-4 pb-4 max-h-72 overflow-y-auto">{e.body}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Unterschrift label={isKidsLike ? 'Unterschrift Mitglied (Kind)' : 'Unterschrift Mitglied'} wert={form.unterschrift_mitglied} onChange={v => updateForm('unterschrift_mitglied', v)} tid="sig-mitglied" />
            {isKidsLike && <Unterschrift label="Unterschrift Erziehungsberechtigte/r" wert={form.unterschrift_eltern} onChange={v => updateForm('unterschrift_eltern', v)} tid="sig-eltern" />}
            <p className="text-[11px] text-muted-foreground">Nach dem Abschluss geht der Vertrag als PDF mit Unterschriften, Bestätigungen und SEPA-Mandat automatisch an die E-Mail-Adresse{isKidsLike ? 'n von Mitglied und Erziehungsberechtigte/r' : ' des Mitglieds'}.</p>
          </div>

          <button onClick={handleSubmit} disabled={submitting || uvSendet || !!uvVorgang}
            className={`w-full flex items-center justify-center gap-2 text-white text-base font-bold py-4 rounded-xl transition-colors disabled:opacity-50 shadow-lg ${isKidsLike ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-marke hover:bg-marke-dunkel'}`}
            data-testid="btn-submit">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Wird erstellt...</> : <><FileSignature className="w-5 h-5" /> Vertrag abschließen</>}
          </button>
        </div>
      )}

      {uvVorgang && (
        <SendenDialog vorgang={uvVorgang} sendet={uvSendet} onSchliessen={() => { uvStand.current = null; setUvVorgang(null); }}
          onErneut={sendeZurUnterschrift} onFertig={uvFertig} />
      )}

      {/* ══════════ SCHRITT 5: Follow-up / Abschluss ══════════ */}
      {step === 5 && (
        <div className="max-w-2xl space-y-6" data-testid="step-5-followup">

          {/* MODE A: Follow-up ONLY */}
          {isFollowUpOnly && !result?.followUpSaved && (
            <>
              <button onClick={() => goStep(3)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="btn-back-5">
                <ArrowLeft className="w-4 h-4" /> Zurück zum Angebot
              </button>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Follow-up planen</h2>
                <p className="text-sm text-muted-foreground mt-1">Kein Abschluss am Tag 1 — Lead nachfassen</p>
              </div>

              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="einstieg-hinweis">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <div className="text-sm font-bold text-amber-800">Kein Tag-1-Rabatt</div>
                  <p className="text-xs text-amber-700 mt-0.5">Ohne Abschluss heute entfällt der Tag-1-Bonus von {fmt(einstiegSatz.regulaer - einstiegSatz.tag1)} €. Der Betreute Einstieg kostet dann regulär <span className="font-bold">{fmt(einstiegSatz.regulaer)} €</span>.</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-2">
                <h3 className="text-sm font-bold flex items-center gap-2"><User className="w-4 h-4 text-marke" /> Lead-Daten</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-muted-foreground">Name</div><div className="font-medium">{leadName || '—'}</div>
                  <div className="text-muted-foreground">Telefon</div><div className="font-medium">{leadPhone || '—'}</div>
                  <div className="text-muted-foreground">E-Mail</div><div className="font-medium">{leadEmail || '—'}</div>
                  <div className="text-muted-foreground">Tarif-Interesse</div><div className="font-medium">{tarif?.name} ({durationLabel})</div>
                  <div className="text-muted-foreground">4-Wo.-Beitrag</div><div className="font-bold text-marke">{fmt(vierWochen)} €</div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-4">
                <h3 className="text-sm font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Follow-up Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <VInput label="Follow-up Datum *" type="date" v={followUp.date} set={v => updateFU('date', v)} tid="fu-date" />
                  <VInput label="Uhrzeit" type="time" v={followUp.time} set={v => updateFU('time', v)} tid="fu-time" />
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">Kanal</label>
                    <select value={followUp.channel} onChange={e => updateFU('channel', e.target.value)} data-testid="fu-channel" className="w-full h-10 px-3 bg-white border border-border/60 rounded-lg text-sm">
                      {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">Priorität</label>
                    <select value={followUp.priority} onChange={e => updateFU('priority', e.target.value)} data-testid="fu-priority" className="w-full h-10 px-3 bg-white border border-border/60 rounded-lg text-sm">
                      {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <VInput label="Betreff" v={followUp.subject} set={v => updateFU('subject', v)} tid="fu-subject" ph="z.B. Probetraining nachfragen" span2 />
                <textarea value={followUp.notes} onChange={e => updateFU('notes', e.target.value)} rows={2} placeholder="Notizen zum Follow-up..." data-testid="fu-notes" className="w-full px-3 py-2 bg-white border border-border/60 rounded-lg text-sm resize-none" />
              </div>

              <div className="flex gap-3">
                <button onClick={() => goStep(3)} className="flex-1 flex items-center justify-center gap-2 border-2 border-border/60 text-muted-foreground font-medium py-3 rounded-xl hover:bg-muted/20 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Zurück
                </button>
                <button onClick={handleSaveFollowUp} disabled={savingFollowUp} className="flex-1 flex items-center justify-center gap-2 bg-amber-500 text-white font-bold py-3 rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50 shadow-lg" data-testid="btn-save-followup">
                  {savingFollowUp ? <><Loader2 className="w-4 h-4 animate-spin" /> Speichere...</> : <><Save className="w-4 h-4" /> Lead & Follow-up speichern</>}
                </button>
              </div>
            </>
          )}

          {/* Follow-up gespeichert */}
          {isFollowUpOnly && result?.followUpSaved && (
            <>
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto"><Clock className="w-8 h-8 text-amber-500" /></div>
                <div>
                  <h2 className="text-2xl font-bold">Follow-up gespeichert!</h2>
                  <p className="text-sm text-muted-foreground mt-1">{result.vorhanden ? `Der vorhandene Lead „${result.leadName}" wurde ergänzt, es entstand keine Dublette` : `Lead „${result.leadName}" wurde angelegt mit Follow-up`}</p>
                </div>
                <div className="bg-white rounded-xl p-5 text-left space-y-2 text-sm border border-border/60">
                  <div className="flex justify-between"><span className="text-muted-foreground">Lead</span><span className="font-medium">{result.leadName}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tarif-Interesse</span><span className="font-medium">{result.planName} ({result.planDuration})</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Follow-up</span><span className="font-medium">{followUp.date} um {followUp.time} Uhr</span></div>
                </div>
              </div>
              <button onClick={resetAll} className="w-full flex items-center justify-center gap-2 bg-marke text-white font-bold py-3 rounded-xl hover:bg-marke-dunkel transition-colors" data-testid="btn-new-sale-fu">
                <ClipboardList className="w-4 h-4" /> Neuen Verkauf starten
              </button>
            </>
          )}

          {/* MODE B: Vertrag erstellt */}
          {!isFollowUpOnly && result && (
            <>
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-500" /></div>
                <div>
                  <h2 className="text-2xl font-bold">Vertrag erstellt!</h2>
                  <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
                </div>
                <div className="bg-white rounded-xl p-5 text-left space-y-2 text-sm border border-border/60">
                  <div className="flex justify-between"><span className="text-muted-foreground">Nr.</span><span className="font-mono font-bold">{result.vertrag?.vertragsnummer}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mitglied</span><span className="font-medium">{result.vertrag?.full_name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tarif</span><span className="font-medium">{result.vertrag?.plan_name} ({result.vertrag?.duration_label})</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Monatsbeitrag</span><span className="font-bold text-marke">{fmt(result.vertrag?.monthly_price || 0)} €</span></div>
                  {result.vertrag?.gym_sauna && !result.vertrag?.gym_sauna_inklusive && <div className="flex justify-between"><span className="text-muted-foreground">Gym & Sauna Add-on</span><span className="font-medium">+{fmt((result.vertrag?.gym_sauna_wochenpreis || 0) * 4)} €/Monat · gesamt {fmt(result.vertrag?.monthly_price_gesamt || 0)} €</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Betreuter Einstieg</span><span className="font-bold text-emerald-600">{fmt(result.vertrag?.betreuter_einstieg_betrag || 0)} €{result.vertrag?.tag1_angebot ? ' (Tag 1)' : ''}</span></div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-border/60 p-5 space-y-4">
                <h3 className="text-sm font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-marke" /> Nächsten Schritt festlegen (optional)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <VInput label="Follow-up Datum" type="date" v={followUp.date} set={v => updateFU('date', v)} tid="fu-date-c" />
                  <VInput label="Uhrzeit" type="time" v={followUp.time} set={v => updateFU('time', v)} tid="fu-time-c" />
                  <VInput label="Betreff" v={followUp.subject} set={v => updateFU('subject', v)} tid="fu-subject-c" ph="z.B. Einweisung planen" span2 />
                </div>
                <button onClick={speichereNaechstenSchritt} disabled={savingFollowUp || schrittGespeichert} data-testid="fu-speichern-c"
                  className="w-full flex items-center justify-center gap-2 border border-border/60 text-sm font-bold py-2.5 rounded-xl hover:bg-muted/20 disabled:opacity-60">
                  {schrittGespeichert ? <><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Gespeichert</> : <><Save className="w-4 h-4" /> Nächsten Schritt speichern</>}
                </button>
              </div>

              {result?.pdf_versand && (
                <p className={`text-xs rounded-lg px-3 py-2 ${result.pdf_versand.status === 'gesendet' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="pdf-versand">
                  {result.pdf_versand.status === 'gesendet'
                    ? `Vertrag als PDF gesendet an ${(result.pdf_versand.empfaenger || []).filter(e => e.status === 'gesendet').map(e => e.an).join(', ')}.`
                    : `PDF wurde nicht per E-Mail gesendet (${result.pdf_versand.grund || result.pdf_versand.status || 'unbekannt'}) — bitte herunterladen und dem Mitglied mitgeben.`}
                </p>
              )}
              <div className="flex gap-3">
                <button onClick={() => { const vid = result?.vertrag?.id; if (vid) window.open(`${API}/api/vertraege/${vid}/pdf`, '_blank'); }}
                  className="flex-1 flex items-center justify-center gap-2 border-2 border-marke text-marke font-bold py-3 rounded-xl hover:bg-marke/5 transition-colors" data-testid="btn-download-pdf">
                  <Download className="w-4 h-4" /> Vertrag als PDF
                </button>
                <button onClick={resetAll} className="flex-1 flex items-center justify-center gap-2 bg-marke text-white font-bold py-3 rounded-xl hover:bg-marke-dunkel transition-colors" data-testid="btn-new-sale">
                  <ClipboardList className="w-4 h-4" /> Neuen Verkauf starten
                </button>
              </div>
            </>
          )}

          {!isFollowUpOnly && !result && (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /><p className="text-sm text-muted-foreground mt-3">Vertrag wird verarbeitet...</p></div>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}

/* ── Helper Components ── */
function VInput({ label, v, set, type = 'text', tid, ph, span2 }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className="text-[11px] font-medium text-muted-foreground block mb-1">{label}</label>
      <input type={type} value={v} onChange={e => set(e.target.value)} placeholder={ph} data-testid={tid}
        className="w-full h-10 px-3 bg-white border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-marke/20 focus:border-marke/30" />
    </div>
  );
}

function Consent({ checked, onChange, tid, text }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer bg-white rounded-xl p-4 border border-border/60">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-marke mt-0.5 w-4 h-4" data-testid={tid} />
      <span className="text-xs text-muted-foreground">{text}</span>
    </label>
  );
}
